import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Popover, Space, Tag, Tooltip } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import DeleteTopPlanApi from "../api/DeleteTopPlanApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../helpers/NotificationHelpers";
import { permissionAbility } from "../helpers/permissionHelpers";
import { getAmountInText, getNumberInText } from "../helpers/targetHelpers";
import { getProgressColor } from "../utils/progressColors";
import HardDeleteModal from "./modal/HardDeleteModal";
import { useEffect } from "react";
import SplitPlanInfoModal from "./modal/info/SplitPlanInfoModal";
import EditSplitPlanModal from "./modal/edit/EditSplitPlanModal";
import SplitPlanSpace from "./top-plan/SplitPlanSpace";

function MyPlanElement(props) {
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletePlanId, setDeletePlanId] = useState("");
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const [activeKeys, setActiveKeys] = useState([]);
  const [collapse, setCollapse] = useState(true);
  const [canCollapse, setCanCollapse] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState();
  const [infoModal, setInfoModal] = useState(false);
  const [targetAchived, setTargetAchieved] = useState(0);
  const [performance, setPerformance] = useState(0);


  useEffect(() => {
    if (props.data?.canUpdate) {
      setCanCollapse(false);
    } else {
      setCanCollapse(true);
    }
  }, [props.data]);

  useEffect(() => {
    if (targetAchived) {
      setPerformance(((targetAchived / props.data?.target) * 100).toFixed(2));
    }
  }, [targetAchived, props.data?.target]);

  // Update performance when props.data changes (for immediate updates)
  useEffect(() => {
    if (props.data?.performance !== undefined) {
      setPerformance(props.data.performance);
    }
  }, [props.data?.performance]);

  function getTargetString(target, scale, abb) {
    let targetString = "";
    if (scale === "Number") {
      targetString = getNumberInText(target);
    } else if (scale === "Currency") {
      targetString = getAmountInText(target, abb);
    } else if (scale === "Percentage") {
      targetString = parseFloat(target).toFixed(2) + "%";
    } else {
      targetString = target + abb;
    }
    return targetString;
  }

  async function deletePlan() {
    const apiResponse = await DeleteTopPlanApi(deletePlanId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Top Plan deleted");

      props.handleDelete(deletePlanId);
      props.handleDeleteChange(props.data?.planId, props.totalChildCount - 1);
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setDeletePlanId("");
  }

  function handleExpand(element) {
    setActiveKeys([props.data?.planId]);
  }

  function handleDeleteChange(planId, totalChildCount) {
    totalChildCount === 0 && setCanCollapse(false);
  }

  return (
    <>
      <div className="px-4">
        <div 
          className={"flex items-center rounded-md " + ((props.data?.isActive === 0 || props.data?.isActive === false || props.data?.hasInactiveAncestor === true) ? "cursor-not-allowed" : "cursor-pointer")} 
          onClick={() => { 
            // Don't open modal if plan is inactive OR if any ancestor is inactive
            const isEffectivelyInactive = props.data?.isActive === 0 || props.data?.isActive === false || props.data?.hasInactiveAncestor === true;
            if (!isEffectivelyInactive) {
              setSelectedPlan(props.data);
              setInfoModal(true);
            }
          }}
        >
          <div className="flex justify-center items-center w-8">
            {canCollapse ? (
              collapse ? (
                <PlusSquareOutlined
                  className="cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleExpand(props.data?.planId);
                    setCollapse(!collapse);
                  }}
                />
              ) : (
                <MinusSquareOutlined
                  className="cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCollapse(!collapse);
                  }}
                />
              )
            ) : (
              ""
            )}
          </div>
          <div className={"grid grid-cols-5 ml-2 p-3 border gap-5 w-full" + ((props.data?.isActive === 0 || props.data?.isActive === false || props.data?.hasInactiveAncestor === true) ? " opacity-50 bg-gray-100" : "")}>
            <div className="col-span-3">
              <div className="mb-1">
                {props.data?.title}
              </div>
              <div className="flex items-center gap-1">
                <div>
                  <Tag
                    color={getProgressColor(performance || props.data?.performance || 0)}
                  >
                    {parseFloat(performance || props.data?.performance || 0).toFixed(2) + "%"}
                  </Tag>
                </div>
                <div>
                  {getTargetString(
                    props.data?.target,
                    props.data?.scale?.scaleName,
                    props.data?.scale?.scaleUnitAbbreviation
                  )}
                </div>
              </div>
            </div>

            <div className="col-span-2 flex justify-between items-start">
              <div>
                <div className="flex items-center">
                  {props.data?.assignedTo?.imageUrl ? (
                    <Avatar
                      style={{ minWidth: "32px" }}
                      className="cursor-pointer"
                      src={props.data?.assignedTo?.imageUrl}
                    />
                  ) : (
                    <Avatar style={{ minWidth: "32px" }}>
                      {props.data?.assignedTo?.firstName ? (
                        props.data?.assignedTo?.firstName
                          ?.charAt(0)
                          .toUpperCase()
                      ) : (
                        <UserOutlined />
                      )}
                    </Avatar>
                  )}
                  <div className="ml-2">
                    <div>{props.data?.assignedTo?.firstName || "-"}</div>
                    <div className="flex items-center gap-1 -mt-1">
                      <div className="text-xs ">
                        {props.data?.assignedTo?.designationAbb},
                      </div>
                      <div className="flex items-center">
                        <div
                          className="min-w-[8px] h-2 mr-1 rounded-full"
                          style={{
                            backgroundColor: props.data?.unit?.orgLevelColor,
                          }}
                        />
                        <div className="truncate">{props.data?.unit?.name}</div>
                        <Popover
                          placement="top"
                          content={
                            <div className="text-xs">
                              {props.data?.unit?.name}
                              {props.data?.unit?.headUnit &&
                                " < " + props.data?.unit?.headUnit?.name}
                              {props.data?.unit?.headUnit?.headUnit &&
                                " < " +
                                  props.data?.unit?.headUnit?.headUnit?.name}
                            </div>
                          }
                          trigger="hover"
                        >
                          <div className="text-gray-500 text-xs ml-1">
                            <InfoCircleOutlined />
                          </div>
                        </Popover>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Space size="middle">
                {permissionAbility(userLevel, "plans", "edit") && (
                  <Tooltip title={"Edit"}>
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedPlan(props.data);
                        setEditModal(true);
                      }}
                      className="text-blue-500 hover:text-blue-600 cursor-pointer"
                    >
                      <EditOutlined />
                    </div>
                  </Tooltip>
                )}
                {permissionAbility(userLevel, "plans", "delete") &&
                  (!canCollapse ? (
                    <Tooltip title={"Delete"}>
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeletePlanId(props.data?.planId);
                          setDeleteModal(true);
                        }}
                        className="text-red-500 hover:text-red-600 cursor-pointer"
                      >
                        <DeleteOutlined />
                      </div>
                    </Tooltip>
                  ) : (
                    <Popover
                      placement="left"
                      content={
                        <div className="text-xs w-48">
                          Can't delete this element. This plan was cascaded.
                        </div>
                      }
                      trigger="hover"
                    >
                      <div className="text-gray-500">
                        <DeleteOutlined />
                      </div>
                    </Popover>
                  ))}
              </Space>
            </div>
          </div>
        </div>
        <div className={"" + (collapse ? "hidden" : "block")}>
        <SplitPlanSpace
            planId={props.data?.planId}
            activeKeys={activeKeys}
            collapse={collapse}
            handleDeleteChange={(totalChildCount) =>
              handleDeleteChange(totalChildCount)
            }
            onParentRefresh={props.onParentRefresh}
          />
        </div>
      </div>

      <EditSplitPlanModal
        visible={editModal}
        handleCancel={() => {
          setSelectedPlan();
          setEditModal(false);
        }}
        plan={selectedPlan}
        handleOk={(data) => props.handleEdit(data)}
      />

      <HardDeleteModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal(false)}
        handleOk={() => {
          deletePlan();
        }}
        text={`Are you sure want to delete this ${topPlanState}?`}
        title={`Delete ${topPlanState}`}
      />
    
      <SplitPlanInfoModal
        visible={infoModal}
        plan={selectedPlan}
        handleCancel={() => {
          setInfoModal(false);
          setSelectedPlan();
        }}
        updateTarget={(target) => {
          setTargetAchieved(target);
          // Trigger immediate re-render by updating the performance
          const newPerformance = ((target / selectedPlan?.target) * 100).toFixed(2);
          setPerformance(newPerformance);
        }}
      />
    </>
  );
}

export default MyPlanElement;
